import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { sanitizeInput, checkRateLimit, logSecurityEvent } from '@/lib/security';

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Rate limiting check for contact form
      const rateLimitPassed = await checkRateLimit('contact_form')
      if (!rateLimitPassed) {
        toast({
          title: "Rate limit exceeded",
          description: "Please wait before sending another message.",
          variant: "destructive",
        })
        return
      }

      // Sanitize input data
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: formData.email.toLowerCase().trim(),
        subject: sanitizeInput(formData.subject),
        message: sanitizeInput(formData.message),
        user_id: user?.id || null
      }

      // Validate sanitized data
      if (!sanitizedData.name || !sanitizedData.email || !sanitizedData.message) {
        toast({
          title: "Validation error",
          description: "Please fill in all required fields.",
          variant: "destructive",
        })
        return
      }

      const { error } = await supabase
        .from('contact_messages')
        .insert(sanitizedData);

      if (error) throw error;

      // Log security event
      await logSecurityEvent('contact_form_submitted', {
        hasUser: !!user?.id,
        messageLength: sanitizedData.message.length
      }, user?.id)

      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you as soon as possible.",
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="max-w-lg mx-auto border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-success rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Thank You!</CardTitle>
          <CardDescription>
            Your message has been sent successfully. We'll get back to you within 24 hours.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => setIsSubmitted(false)}
            variant="outline" 
            className="w-full"
          >
            Send Another Message
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-gradient-primary rounded-2xl mx-auto mb-4 flex items-center justify-center">
          <MessageSquare className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-3xl bg-gradient-hero bg-clip-text text-transparent">
          Get in Touch
        </CardTitle>
        <CardDescription className="text-lg">
          Have questions, feedback, or need help? We'd love to hear from you!
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-background/50 border-border/50"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-background/50 border-border/50"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              type="text"
              placeholder="What's this about?"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              className="bg-background/50 border-border/50"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Tell us more about your inquiry..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={6}
              className="bg-background/50 border-border/50 resize-none"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-gradient-primary hover:opacity-90 text-white"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>
        
        <div className="mt-8 pt-6 border-t border-border/20">
          <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>We respond within 24 hours</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};